'use client';

import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useEffect, useState } from 'react';
import { AbbMoreDots } from '@/assets/icons/components/abb-more-dots';
import { AbbTime } from '@/assets/icons/components/abb-time';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useCheckTranslatedString } from '@/modules/utils/custom-hooks/use-check-translated-string';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { downloadFile } from '@/modules/utils/download-file';
import { Dropdown } from '@/ui-components/dropdown/dropdown';
import { NestedDropdownItem } from '@/ui-components/dropdown/hooks/nested-dropdown-item';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { LabelBadge } from '@/ui-components/label-badge/label-badge';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { Tooltip } from '@/ui-components/tooltip/tooltip';
import { useDocumentApi } from '../../hooks/use-document-api';
import { useOpenSharedDocument } from '../../hooks/use-open-shared-document';
import type { DocumentRowProps } from '../types';
import { downloadProjectDocumentApi } from '../../api/document.api';
import { DOCUMENT_TYPE_EXTENSION } from '../../document.const';

export const DocumentRow: FunctionComponent<DocumentRowProps> = ({
	document: projectDocument,
	projectsOptions,
	documentStatuses,
	showHistory,
}) => {
	const t = useTranslations();
	const { setProjectContentTabIndex, showToast, project } = useAppContext();
	const { name, documentId, documentType, projectStatus, region } = projectDocument;
	const [selectedStatus, setSelectedStatus] = useState(projectStatus);
	const { updateProjectDocumentStatus, duplicateProjectDocument, moveProjectDocument } = useDocumentApi();
	const { openSharedDocument } = useOpenSharedDocument({ projectDocumentId: documentId, accessLevel: 'edit' });
	const { addSearchParam } = useNavigation();
	const { isDarkTheme } = useTheme();
	const checkTranslatedString = useCheckTranslatedString();

	useEffect(() => {
		if (!projectStatus) return;

		setSelectedStatus(projectStatus);
	}, [projectStatus]);

	if (!projectDocument) return null;

	const handleOpenModal = (modalName: string) => {
		addSearchParam([
			{ key: 'modal', value: modalName },
			{ key: 'id', value: documentId },
			{ key: 'title', value: name ?? '' },
		]);
	};

	const statuses = documentStatuses?.map((status) => ({
		label: (
			<span className='text-sm w-full text-left px-4 py-3'>{status.isDefault ? checkTranslatedString(status.name) : status.name}</span>
		),
		value: status.documentId,
		onClick: () => {
			setSelectedStatus(status);
			updateProjectDocumentStatus.mutate({
				body: { documentId: status.documentId },
				projectDocumentId: documentId,
			});
		},
	}));

	const handleDeleteAlert = () => {
		addSearchParam([
			{ key: 'modal', value: 'Alert' },
			{ key: 'id', value: documentId },
			{ key: 'type', value: 'document' },
		]);
	};

	const downloadDocument = async (documentId: Data.Entity<'api::project-document.project-document'>['documentId']) => {
		const { data } = await downloadProjectDocumentApi(documentId);

		if (!data.downloadUrl) {
			showToast({ message: t('T_DOCUMENT_FILE_NOT_FOUND'), variant: 'error' });
			return;
		}

		const response = await fetch(data.downloadUrl);
		const blobFile = await response.blob();
		const docTypeTitle = projectDocument.documentType?.title as keyof typeof DOCUMENT_TYPE_EXTENSION;
		const fileType = docTypeTitle && DOCUMENT_TYPE_EXTENSION[docTypeTitle] ? DOCUMENT_TYPE_EXTENSION[docTypeTitle] : undefined;

		downloadFile({ file: blobFile, fileName: projectDocument?.name || '', type: fileType });
	};

	const filteredProjectsOptions = projectsOptions?.filter((proj) => proj.value !== project?.documentId);

	const menuItems = [
		{
			value: 'SHARE',
			label: (
				<span
					className='text-dark text-sm font-normal dark:text-n700_text_dark_mode w-full text-left px-4 py-3'
					onClick={() => handleOpenModal('SharedDocumentsModal')}
				>
					{t('T_SHARE')}
				</span>
			),
		},
		{
			value: 'RENAME',
			label: (
				<span
					className='text-dark text-sm font-normal dark:text-n700_text_dark_mode w-full text-left px-4 py-3'
					onClick={() => handleOpenModal('ProjectDocumentModal')}
				>
					{t('T_RENAME')}
				</span>
			),
		},
		{
			value: 'DOWNLOAD_FILE',
			label: (
				<span
					className='text-dark text-sm font-normal dark:text-n700_text_dark_mode w-full text-left px-4 py-3'
					onClick={() => downloadDocument(documentId)}
				>
					{t('T_DOWNLOAD_FILE')}
				</span>
			),
		},
		{
			value: 'DUPLICATE',
			label: (
				<span className='text-dark text-sm font-normal dark:text-n700_text_dark_mode w-full text-left px-4 py-3'>{t('T_DUPLICATE')}</span>
			),
			onClick: () => duplicateProjectDocument.mutate({ documentId }),
		},
		...(projectsOptions && projectsOptions?.length > 1
			? [
					{
						value: 'MOVE',
						label: (
							<NestedDropdownItem
								label={t('T_MOVE_TO')}
								items={filteredProjectsOptions ?? []}
								onClick={(value) => moveProjectDocument.mutate({ documentId, projectDocumentId: value })}
							/>
						),
						onClick: () => {},
					},
				]
			: []),
		{
			value: 'DELETE',
			label: (
				<span
					className='!text-brand text-sm font-normal dark:text-n700_text_dark_mode w-full text-left px-4 py-3'
					onClick={handleDeleteAlert}
				>
					{t('T_DELETE')}
				</span>
			),
		},
	];

	return (
		<Flexbox items='center' justify='between' className='p-2 gap-2 dark:bg-card_dark'>
			<Flexbox justify='center' items='center' className='w-10 h-10 aspect-4/3 rounded-[4px] bg-n600_text relative'>
				<img src={documentType?.icon?.url} alt={name ?? ''} className='w-full h-full object-contain p-[5px]' />
				{region && (
					<span className='absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 bg-red-600 text-white text-[10px] font-semibold px-1 rounded'>
						{region}
					</span>
				)}
			</Flexbox>

			<Tooltip content={t('T_OPEN_DOCUMENT')} style='dark'>
				<Paragraph
					className='w-[150px] cursor-pointer hover:underline'
					variant='text-sm'
					onClick={() => openSharedDocument({ documentId, documentTypeName: documentType?.title, region })}
				>
					{name}
				</Paragraph>
			</Tooltip>

			<Flexbox items='center' justify='center' className='gap-2'>
				<Tooltip content={t('T_CHANGE_STATUS')} style='dark'>
					<Dropdown
						isMenu
						label={
							<LabelBadge
								text={checkTranslatedString(selectedStatus?.name)}
								color={selectedStatus?.color}
								size='xsmall'
								background={`${selectedStatus?.color}20`}
								className='max-w-5'
								pill
							/>
						}
						inline
						arrowIcon={false}
						menuItems={statuses ?? []}
					/>
				</Tooltip>

				<Tooltip content={t('T_SHOW_DOCUMENT_HISTORY')} style='dark'>
					<AbbTime
						size={24}
						fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
						className='cursor-pointer'
						onClick={() => {
							showHistory({ documentId, name });
							setProjectContentTabIndex(1);
						}}
					/>
				</Tooltip>
				<Dropdown
					label={<AbbMoreDots size={24} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} className='cursor-pointer mr-1' />}
					inline
					isMenu
					arrowIcon={false}
					menuItems={menuItems}
				/>
			</Flexbox>
		</Flexbox>
	);
};
